import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Card, Form } from "react-bootstrap";
import { useAlert } from "../../../context/AlertContext";
import { useLoading } from "../../../context/LoadingContext";
import { getServiceRequest } from "../../../commons/ServiceRequest";
import { initializeSetOption } from "../../../utils/InitializeSetDefault";
import { emptyValueObjects, findItemInArray } from "../../../utils/ManageArray";
import TableForm from "../../TableForm";
import ConfirmModal from "../../ConfirmModal";
import McTypeService from "../../../services/mctype.service";

const CreateBranchSerialNumberForm = ({ onSubmitCallback }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [mcType, setMCType] = useState([]);
  const [data, setData] = useState({ Array: [] });
  const columns = [
    {
      name: "Item.Item_Code",
      label: "รหัสสินค้า",
      readOnly: true,
      findValue: { data: state?.contract, key: "Item_Id", value: "Item_Id" }
    },
    {
      name: "Item.Item_Name_TH",
      label: "รายละเอียดสินค้า",
      readOnly: true,
      findValue: { data: state?.contract, key: "Item_Id", value: "Item_Id" }
    },
    {
      name: "Item.Serial_No",
      label: "หมายเลขเครื่อง (Serial No.)",
      readOnly: true,
      findValue: { data: state?.contract, key: "Item_Id", value: "Item_Id" }
    },
    {
      name: "Mc_No",
      label: "หมายเลขเครื่องประจำสาขา",
      readOnly: true,
      plusString: ["Cust_NumBranch", "Mc_Type", "Mc_Running"]
    },
    {
      name: "Cust_NumBranch",
      label: "เลขที่สาขา",
      findValue: {
        data: state?.contract,
        key: "CustContract.Cus_Id",
        value: "Cus_Id"
      }
    },
    {
      name: "Mc_Type",
      label: "Machine Type",
      type: "select",
      noneAsync: true,
      options: initializeSetOption({
        key: "MC_Type",
        dataset: mcType.filter(({ Item_Id }) => Item_Id === state?.row.Item_Id)
      })
    },
    { name: "Mc_Running", label: "MC Running", readOnly: true },
    { name: "Seq_Work", label: "Seq.Work" }
  ];

  const checkValidity = () => {
    if (emptyValueObjects(data.Array, ["Mc_Location"])?.length > 0) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณากรอกข้อมูลให้ครบถ้วน",
        type: "danger"
      });
      return false;
    }
    return true;
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity() || !checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(true);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback) {
      const updatedData = dataCallback.Array.map((row) => ({
        ...row,
        Mc_No: row.Cust_NumBranch + row.Mc_Type + row.Mc_Running
      }));
      const flattenedData = state?.data
        .map((item) =>
          item.Item_Id === state?.row?.Item_Id
            ? { ...item, ProdMCNo: updatedData }
            : item
        )
        .flatMap(({ ProdMCNo, Mc_Type, ...item }) =>
          ProdMCNo?.length
            ? ProdMCNo.map(({ Mc_Type, ...childArray }) => ({
                ...childArray
              }))
            : { ...item }
        );

      if (flattenedData) onSubmitCallback(flattenedData);
    }
  };

  useEffect(() => {
    if (state?.data?.length && state?.row) {
      setData({
        Array: state?.data
          .filter(
            ({ Item_Id, Cus_Id }) =>
              Item_Id === state?.row?.Item_Id && Cus_Id === state?.row?.Cus_Id
          )
          .map((item, idx) => ({
            ...item,
            Mc_Type:
              findItemInArray(mcType, "Id", item.Mc_Type_Id)?.MC_Type || "",
            Mc_Running: `0${idx + 1}`,
            Cust_NumBranch:
              findItemInArray(state?.cusBranch, "Cus_Id", state?.row?.Cus_Id)
                ?.Customer?.Cus_NumBranch || ""
          }))
      });
    }
  }, [state]);

  useEffect(() => {
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: McTypeService.getAllMcTypes(),
      callback: (res) => setMCType(res)
    });
  }, []);

  return (
    <Form noValidate validated={validated} onSubmit={handleValidated}>
      <Card className="m-1 mb-4 p-4 border-0">
        <Card.Header className="p-3 bg-transparent border-bottom-0">
          <Card.Title className="mb-0 fs-2">
            สร้างหมายเลขเครื่องประจำสาขา
          </Card.Title>
        </Card.Header>

        <Card.Body>
          <TableForm
            data={data.Array}
            columns={columns}
            defaultData={state?.row}
            onChangeCallback={(newData) =>
              setData({
                Array: newData.map((row, idx) => ({
                  ...row,
                  Mc_Type_Id:
                    findItemInArray(mcType, "MC_Type", row.Mc_Type)?.Id || "",
                  Mc_No: row.Cust_NumBranch + row.Mc_Type + row.Mc_Running,
                  Mc_Running: `0${idx + 1}`
                }))
              })
            }
          />
        </Card.Body>

        <Card.Footer className="p-3 text-end border-top-0 bg-transparent">
          <Button
            type="submit"
            variant="success"
            className="me-2 px-5 py-2 text-dark rounded-pill"
          >
            บันทึกข้อมูล
          </Button>
          <ConfirmModal
            show={showModal}
            data={data}
            content="save"
            onSubmitCallback={handleSubmit}
          />

          <Button
            variant="outline-success"
            className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
            onClick={() => navigate("/PM_work/bind/create", { state })}
          >
            ยกเลิก
          </Button>
        </Card.Footer>
      </Card>
    </Form>
  );
};

CreateBranchSerialNumberForm.propTypes = { onSubmitCallback: PropTypes.func };

export default CreateBranchSerialNumberForm;
